import { Component, Input, Output, OnInit, EventEmitter, OnDestroy, inject } from '@angular/core';

import { LocaleService } from '@next/core-lib/i18n';
import { SiteState } from '@next/core-lib/site';
import { Store, select } from '@ngrx/store';

import { Subject, takeUntil } from 'rxjs';

import { TreatmentTypes } from 'src/app/overview/models';

import * as OverviewSelectors from '../../../store/selectors';

import { CropAid, DeliveryPeriodMomentType, OrderLine, OrderLineStatus, OrderPlanOverviewV2, Package, PeriodOrderPlan } from '../../models';

@Component({
  selector: 'app-period-column',
  templateUrl: './period-column.component.html',
  styleUrls: ['./period-column.component.scss'],
  standalone: false,
})
export class PeriodColumnComponent implements OnInit, OnDestroy {
  private readonly store = inject<Store<SiteState>>(Store);
  private readonly locale = inject(LocaleService);

  @Input() row: OrderPlanOverviewV2;
  @Input() columnOrderPlanPeriod: PeriodOrderPlan;

  @Output() orderLineValueChanged = new EventEmitter<OrderLine>();

  orderLine: OrderLine;
  orderLineOpened = false;

  orderplanOptimized$ = this.store.pipe(select(OverviewSelectors.selectOptimizationStatus));

  cropAids: CropAid[];
  packages: Package[];
  productHasPackages = false;
  orderplanOptimized = false;

  private readonly destroyed$ = new Subject<boolean>();

  ngOnInit(): void {
    this.getCropAids();
    this.getPackages();
    this.getOrderLinesById();
    this.isOrderlineOpened();
    this.determineIfProductHasPackages();

    this.orderplanOptimized$.pipe(takeUntil(this.destroyed$)).subscribe((optimized) => (this.orderplanOptimized = optimized));
  }

  ngOnDestroy(): void {
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }

  isOrderlineOpened() {
    this.orderLineOpened = this.orderLine && this.orderLine.status === OrderLineStatus.open;
  }

  editOrderLine() {
    if (!this.orderLine) {
      this.orderLine = this.getEmptyOrderLine();
    }
    const newValue = this.columnOrderPlanPeriod?.packageCount - this.getNotOpenCount();
    if (this.orderLine.count === newValue || newValue < 0) {
      return;
    }
    this.orderLine.count = newValue;
    this.orderLineValueChanged.emit(this.orderLine);
  }

  determineIfProductHasPackages() {
    const packages = this.getAvailablePackages(this.row);
    this.productHasPackages = packages.length > 0 ? true : false;
  }

  isOrderLineNotOptimized(): boolean {
    return !this.columnOrderPlanPeriod?.isDisabled && this.orderLineOpened && !this.orderLine?.addedByOptimizer;
  }

  getToolTipText(): string {
    let openText: string = null;
    let inCartText: string = null;
    let orderedText: string = null;

    if (this.columnOrderPlanPeriod?.orderLines) {
      this.columnOrderPlanPeriod.orderLines.forEach((orderLine) => {
        if (orderLine.status === OrderLineStatus.open) {
          openText = `${orderLine.count} ${this.locale.translate('open')}`;
        } else if (orderLine.status === OrderLineStatus.inCart) {
          inCartText = `${orderLine.count} ${this.locale.translate('inCart')}`;
        } else {
          orderedText = `${orderLine.count} ${this.locale.translate('ordered')}`;
        }
      });
    }

    const toolTipText = [openText, inCartText, orderedText].filter((text) => text !== null).join('\n');

    if (this.orderLine?.addedByOptimizer && this.orderLineOpened) {
      return `${this.locale.translate('orderTexts.productOptimizedTooltip')}

        ${toolTipText}`;
    } else if (this.isOrderLineNotOptimized() && this.orderplanOptimized && this.columnOrderPlanPeriod?.packageCount) {
      return `${this.locale.translate('orderTexts.productNotOptimizedTooltip')}

        ${toolTipText}`;
    }

    return toolTipText;
  }

  shouldShowOpenBorder(): boolean {
    return this.hasOrderStatus(OrderLineStatus.open);
  }

  shouldShowInCartBorder(): boolean {
    return !this.shouldShowOpenBorder() && this.hasOrderStatus(OrderLineStatus.inCart);
  }

  shouldShowOrderedBorder(): boolean {
    return !this.shouldShowOpenBorder() && !this.shouldShowInCartBorder() && this.hasOrderStatus(OrderLineStatus.finalized);
  }

  bothIconsVisible(): boolean {
    return this.optimizationIconVisible() && this.infoIconVisible();
  }

  optimizationIconVisible(): boolean {
    return this.orderLine?.addedByOptimizer && this.orderLineOpened;
  }

  infoIconVisible(): boolean {
    return !!this.columnOrderPlanPeriod?.orderLines?.length;
  }

  getNotOpenCount(): number {
    return (
      this.columnOrderPlanPeriod.orderLines?.filter((ol) => ol.status !== OrderLineStatus.open).reduce((total, orderLine) => total + orderLine.count, 0) ?? 0
    );
  }

  private hasOrderStatus(orderLineStatus: OrderLineStatus): boolean {
    return this.columnOrderPlanPeriod.orderLines?.some((ol) => ol.status === orderLineStatus);
  }

  private getOrderLinesById() {
    const openOrderLine = this.columnOrderPlanPeriod?.orderLines?.find((ol) => ol.status === OrderLineStatus.open);

    if (openOrderLine) {
      this.orderLine = {
        ...openOrderLine,
        packageId: this.row.packageId,
        cropAidId: this.row.cropAidId,
        year: this.row.year,
        periodId: this.columnOrderPlanPeriod.periodId,
      };
    } else {
      this.orderLine = this.getEmptyOrderLine();
    }
  }

  private getCropAids() {
    this.store.pipe(takeUntil(this.destroyed$), select(OverviewSelectors.selectCropAids)).subscribe((result) => (this.cropAids = result));
  }

  private getCropAid(cropAidId: number) {
    return this.cropAids?.find(({ id }) => id === cropAidId);
  }

  private getPackages() {
    this.store.pipe(takeUntil(this.destroyed$), select(OverviewSelectors.selectPackages)).subscribe((result) => (this.packages = result));
  }

  private getAvailablePackages(orderline: OrderPlanOverviewV2) {
    const productId = this.getCropAid(orderline.cropAidId)?.productId;
    if (!productId) {
      return [];
    }

    return this.packages.filter((pack) => pack.productId === productId && pack.isActive);
  }

  private getEmptyOrderLine(): OrderLine {
    return {
      id: -1,
      packageId: this.row.packageId,
      count: 0,
      year: null,
      periodId: this.columnOrderPlanPeriod?.periodId,
      status: OrderLineStatus.open,
      deliveryPeriodMoment: DeliveryPeriodMomentType.empty,
      treatmentType: TreatmentTypes.GBM,
      orderedDate: null,
      shipToId: 1,
      addedByOptimizer: false,
      cartId: -1,
      orderedId: -1,
      checkoutId: -1,
    };
  }
}
